import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia, Button, Stack, Chip,
} from '@mui/material';
import {
  Storefront as StorefrontIcon,
  Inventory as InventoryIcon,
  RocketLaunch as RocketIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import { ezpPalette } from '../../components/EzpLayout';
import api from '../../services/api';

export default function EzpHome() {
  const navigate = useNavigate();
  const { t } = useTranslation('ezp');
  const [stats, setStats] = useState({ total_products: 2700, total_categories: 30 });
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    api.get('/ezp/stats').then(res => {
      if (res.data.success) setStats(res.data);
    }).catch(() => {});

    api.get('/ezp/products', { params: { page: 1, limit: 8 } }).then(res => {
      if (res.data.success) setFeaturedProducts(res.data.items || []);
    }).catch(() => {});
  }, []);

  const steps = [
    { icon: <StorefrontIcon sx={{ fontSize: 40 }} />, title: t('steps.browse', '瀏覽商品'), desc: t('steps.browse_desc', '從 2,700+ 商品中挑選你想賣的商品') },
    { icon: <CartIcon sx={{ fontSize: 40 }} />, title: t('steps.select', '選品上架'), desc: t('steps.select_desc', '一鍵匯入到你的 URStore 商店') },
    { icon: <RocketIcon sx={{ fontSize: 40 }} />, title: t('steps.sell', '開始販售'), desc: t('steps.sell_desc', '顧客下單後 EZP 直接出貨給顧客') },
  ];

  return (
    <Box>
      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ezpPalette.primary} 0%, #2E7D32 100%)`,
        color: 'white', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden',
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip label={t('hero.badge', '零庫存創業')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, mb: 2 }} />
              <Typography variant="h2" sx={{ fontWeight: 950, mb: 2, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                {t('hero.title', '2,700+ 商品')}
                <br />
                <Box component="span" sx={{ color: '#FFD54F' }}>
                  {t('hero.title_highlight', '一鍵上架你的網路商店')}
                </Box>
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontWeight: 400, lineHeight: 1.8 }}>
                {t('hero.subtitle', '不用備貨、不用囤庫存。從 EZP 好批選品，一鍵上架到你的 URStore 商店，顧客下單後由 EZP 直接出貨。')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained" size="large"
                  onClick={() => navigate('/products')}
                  sx={{ bgcolor: ezpPalette.secondary, fontWeight: 900, borderRadius: 2, px: 4, py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#E65100' } }}
                >
                  {t('hero.cta', '瀏覽商品')}
                </Button>
                <Button
                  variant="outlined" size="large"
                  href="https://urstore.tw/apply"
                  target="_blank"
                  sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', fontWeight: 700, borderRadius: 2, px: 4, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  {t('hero.cta_secondary', '免費開店')}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Stack direction="row" spacing={3}>
                {[
                  { num: `${stats.total_products.toLocaleString()}+`, label: t('hero.stat_products', '商品') },
                  { num: `${stats.total_categories}+`, label: t('hero.stat_categories', '分類') },
                  { num: '0%', label: t('hero.stat_fee', '平台抽成') },
                ].map((s, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 950, color: '#FFD54F' }}>{s.num}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{s.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 950, textAlign: 'center', mb: 1, color: ezpPalette.text }}>
            {t('how.title', '3 步驟，零庫存開始賣')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', color: ezpPalette.textMuted, mb: 6 }}>
            {t('how.subtitle', 'URStore 不收額外費用，EZP 賺經銷價差')}
          </Typography>
          <Grid container spacing={4}>
            {steps.map((step, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card elevation={0} sx={{ textAlign: 'center', p: 4, border: `1px solid ${ezpPalette.border}`, borderRadius: 3 }}>
                  <Box sx={{ color: ezpPalette.primary, mb: 2 }}>{step.icon}</Box>
                  <Typography variant="overline" sx={{ color: ezpPalette.secondary, fontWeight: 700 }}>
                    Step {i + 1}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{step.title}</Typography>
                  <Typography variant="body2" sx={{ color: ezpPalette.textMuted }}>{step.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured products */}
      {featuredProducts.length > 0 && (
        <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: ezpPalette.bg }}>
          <Container maxWidth="lg">
            <Typography variant="h4" sx={{ fontWeight: 950, textAlign: 'center', mb: 1 }}>
              {t('featured.title', '精選商品')}
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', color: ezpPalette.textMuted, mb: 5 }}>
              {t('featured.subtitle', '看看你能賣什麼')}
            </Typography>
            <Grid container spacing={3}>
              {featuredProducts.slice(0, 8).map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.goods_id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2, border: `1px solid ${ezpPalette.border}`, cursor: 'pointer',
                      transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
                    }}
                    onClick={() => navigate(`/products/${product.goods_id}`)}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={product.image_url || product.goods_img || '/images/placeholder.webp'}
                      alt={product.goods_name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.goods_name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="body2" sx={{ color: ezpPalette.textMuted, textDecoration: 'line-through' }}>
                          ${product.suggested_price}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: ezpPalette.primary }}>
                          ${Math.round(parseFloat(product.shop_price) || 0)}
                        </Typography>
                      </Stack>
                      <Chip
                        label={`${t('featured.profit', '預估利潤')} $${product.estimated_profit}`}
                        size="small"
                        sx={{ mt: 1, bgcolor: '#E8F5E9', color: ezpPalette.primary, fontWeight: 600, fontSize: '0.72rem' }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained" size="large"
                onClick={() => navigate('/products')}
                sx={{ bgcolor: ezpPalette.primary, fontWeight: 700, borderRadius: 2, px: 5, '&:hover': { bgcolor: '#2E7D32' } }}
              >
                {t('featured.view_all', '瀏覽全部商品')}
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      {/* CTA */}
      <Box sx={{ py: { xs: 7, md: 9 }, background: `linear-gradient(135deg, ${ezpPalette.primary} 0%, #2E7D32 100%)`, color: 'white', textAlign: 'center' }}>
        <Container maxWidth="sm">
          <InventoryIcon sx={{ fontSize: 48, mb: 2, color: '#FFD54F' }} />
          <Typography variant="h4" sx={{ fontWeight: 950, mb: 2 }}>
            {t('cta.title', '沒有貨也能開店')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, lineHeight: 1.8 }}>
            {t('cta.desc', '從 EZP 好批選品上架，顧客下單後由 EZP 直接出貨。你只需要專心經營行銷和客戶關係。')}
          </Typography>
          <Button
            variant="contained" size="large"
            href="https://urstore.tw/apply"
            target="_blank"
            sx={{ bgcolor: ezpPalette.secondary, fontWeight: 900, borderRadius: 2, px: 5, py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#E65100' } }}
          >
            {t('cta.button', '立即免費開店')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
